import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {propOr} from 'ramda';

import {
  TelephonyCallRecord,
  useAssignTelephonyCallRecordMutation,
  useUnassignTelephonyCallRecordMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {ApiError} from 'shared/web';

import {handleApiError} from '../utils/handleApiError';

const CONFLICT_STATUS = 409;

/** The pairing endpoint answers 409 only when the call is with another customer than the record. */
const isCustomerMismatchError = (error: ApiError) =>
  propOr(null, 'status', error) === CONFLICT_STATUS;

/**
 * Pairs a call with a record and undoes it. Both resolve to whether the change was made, so the
 * caller refetches what shows the pairing; a customer mismatch shows `customerMismatchMessage`
 * (or a generic text) instead of the backend message.
 */
export function useTelephonyCallRecordPairing() {
  const [assignRecord, assignState] = useAssignTelephonyCallRecordMutation();
  const [unassignRecord, unassignState] = useUnassignTelephonyCallRecordMutation();

  // The call whose pairing is in flight, so only its controls show loading.
  const pendingCallId = match([assignState.isLoading, unassignState.isLoading])
    .with([true, false], () => assignState.originalArgs?.callId ?? null)
    .with([false, true], () => unassignState.originalArgs?.callId ?? null)
    .with([true, true], () => assignState.originalArgs?.callId ?? null)
    .otherwise(() => null);

  const pair = (callId: string, record: TelephonyCallRecord, customerMismatchMessage?: string) =>
    assignRecord({callId, body: record})
      .unwrap()
      .then(() => true)
      .catch((error: ApiError) => {
        if (isCustomerMismatchError(error)) {
          showNotification.error(
            customerMismatchMessage ?? i18n.t('page.telephony.pairing.customerMismatch')
          );
        } else {
          handleApiError(error);
        }

        return false;
      });

  const unpair = (callId: string, record: TelephonyCallRecord) =>
    unassignRecord({callId, ...record})
      .unwrap()
      .then(() => true)
      .catch((error: ApiError) => {
        handleApiError(error);

        return false;
      });

  return {pair, unpair, pendingCallId};
}
